import React, { createRef } from "react";
import Dropzone, {
  DropzoneRef,
  FileRejection,
  DropEvent,
} from "react-dropzone";
import ProgressBar from "react-bootstrap/ProgressBar";
import { backendUrl, client } from "../index";
import gql from "graphql-tag";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Collapse from "react-bootstrap/Collapse";
import { Query } from '@apollo/client/react/components';
import { Link } from "react-router-dom";

import "../styles/OcrProcessDropzone.css";
import "../styles/animation.css";

const dropzoneRef = createRef<DropzoneRef>();

const GET_TOKEN = gql`
  mutation createMobileUpload {
    createMobileUpload {
      token
    }
  }
`;

const UPLOAD_PROGRESS = gql`
  query upload_progress($token: String!) {
    uploadProgress(token: $token) {
      finished
      error
      processNumber
    }
  }
`;

const FINISH_UPLOAD = gql`
  mutation finishMobileUpload($token: String!) {
    finishMobileUpload(token: $token) {
      success
    }
  }
`;

interface OcrProcessDropzoneState {
  files: File[];
  rejected: string[];
  showRejected: boolean;
  showCompleted: boolean;
  showServerProgress: boolean;
  hideRejectedAfter: number;
  uploadToken: string;
  isProcessing: boolean;
  errorMsg: string;
  progress: { state: string; percentage: number }[];
}

export default class OcrProcessDropzone extends React.Component<any> {
  state: OcrProcessDropzoneState;

  constructor(props: any) {
    super(props);

    this.state = {
      files: [],
      showCompleted: true,
      rejected: [],
      showRejected: false,
      showServerProgress: false,
      isProcessing: false,
      hideRejectedAfter: 0,
      progress: [],
      uploadToken: "",
      errorMsg: "",
    };

    this.onDropAccepted = this.onDropAccepted.bind(this);
    this.onDropRejected = this.onDropRejected.bind(this);
    this.removeFileFromList = this.removeFileFromList.bind(this);
    this.startUpload = this.startUpload.bind(this);
    this.uploadFiles = this.uploadFiles.bind(this);
    this.uploadFile = this.uploadFile.bind(this);
  }

  onDropAccepted(files: File[]) {
    this.setState((prev: OcrProcessDropzoneState) => ({
      ...prev,
      files: prev.files.concat(files),
    }));
  }

  onDropRejected(fileRejections: FileRejection[], event: DropEvent) {
    const arr: string[] = this.state.rejected.slice();
    const displayTime = 5100;

    fileRejections.forEach(({ file, errors }) => {
      arr.push(`${file.name} ist keine .png, .jpg, .jpeg oder .pdf-Datei`);
    });

    this.setState((prev: OcrProcessDropzoneState) => ({
      ...prev,
      showRejected: true,
      hideRejectedAfter: Math.max(
        prev.hideRejectedAfter,
        Date.now() + displayTime - 100
      ),
      rejected: arr,
    }));

    setTimeout(() => {
      this.setState((prev: OcrProcessDropzoneState) => ({
        ...prev,
        showRejected: prev.hideRejectedAfter > Date.now(),
        rejected: prev.hideRejectedAfter > Date.now() ? prev.rejected : [],
      }));
    }, displayTime);
  }

  startUpload() {
    const that = this;

    client
      .mutate({ mutation: GET_TOKEN })
      .then((result: any) => {
        that.setState((prev: OcrProcessDropzoneState) => ({
          ...prev,
          uploadToken: result.data.createMobileUpload.token,
          progress: prev.files.map((_, idx) => ({
            state: this.state.progress[idx] ? "done" : "pending",
            percentage: this.state.progress[idx] ? 100 : 0,
          })),
        }));
      })
      .then(that.uploadFiles)
      .catch((error: any) => {
        console.log(error);
        that.setState((prev: OcrProcessDropzoneState) => ({
          ...prev,
          progress: [],
        }));

        if (that.state.errorMsg !== "") {
          alert(that.state.errorMsg);
        } else {
          alert(
            "Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
          );
        }
      });
  }

  uploadFile(file: File, idx: number) {
    if (this.state.progress[idx].state === "done") {
      return Promise.resolve(true);
    }

    const t = this;
    const fname = file.name;

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("token", this.state.uploadToken);
      formData.append("file", file);

      const req = new XMLHttpRequest();

      req.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const copy = this.state.progress.slice();
          copy[idx] = {
            state: "pending",
            percentage: (event.loaded / event.total) * 100,
          };
          this.setState({ progress: copy });
        }
      });

      req.addEventListener("readystatechange", function (e) {
        if (req.readyState === 4 && req.status === 200) {
          resolve(req.response);
        } else if (req.readyState === 4 && req.status === 400) {
          const copy = t.state.progress.slice();
          copy[idx] = { state: "error", percentage: 0 };
          t.setState({
            progress: copy,
            errorMsg: `Es ist ein Fehler aufgetreten. Die Datei ${fname} is vermutlich zu groß.`,
          });
          reject(req.response);
        } else if (req.readyState === 5 && req.status >= 500) {
          const copy = t.state.progress.slice();
          copy[idx] = { state: "error", percentage: 0 };
          t.setState({
            progress: copy,
            errorMsg: "Es ist ein interner Fehler aufgetreten.",
          });
          reject(req.response);
        }
      });

      req.upload.addEventListener("load", (event) => {
        const copy = this.state.progress.slice();
        copy[idx] = { state: "done", percentage: 100 };
        this.setState({ progress: copy });
      });

      req.upload.addEventListener("error", (event) => {
        const copy = this.state.progress.slice();
        copy[idx] = { state: "error", percentage: 0 };
        this.setState({ progress: copy });
        reject(req.response);
      });

      req.open("POST", backendUrl + "mobile_upload");
      req.setRequestHeader("Accept", "application/json");
      req.send(formData);
    });
  }

  uploadFiles() {
    const promises = this.state.files.reduce(
      (chain: any, cur: any, idx: number) => {
        return chain.then(() => this.uploadFile(cur, idx));
      },
      Promise.resolve()
    );

    return promises
      .then(() => {
        client.mutate({
          mutation: FINISH_UPLOAD,
          refetchQueries: ["ocrProcesses"],
          variables: { token: this.state.uploadToken },
        });
      })
      .then(() => {
        setTimeout(
          () =>
            this.setState((prev) => ({
              ...prev,
              showCompleted: false,
              showServerProgress: true,
              isProcessing: true,
            })),
          3000
        );
        setTimeout(
          () =>
            this.setState((prev) => ({
              ...prev,
              showCompleted: true,
              files: [],
              progress: [],
            })),
          3400
        );
      })
      .catch((e: any) => {
        console.log(e);
        this.setState((prev: OcrProcessDropzoneState) => ({
          ...prev,
          uploadToken: "",
          progress: [],
        }));

        if (this.state.errorMsg !== "") {
          alert(this.state.errorMsg);
        } else {
          alert(
            "Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
          );
        }
      });
  }

  removeFileFromList(file: File) {
    this.setState((prev: OcrProcessDropzoneState) => ({
      ...prev,
      files: prev.files.filter((f) => f !== file),
    }));
  }

  render() {
    const isUploading =
      this.state.progress.length > 0 &&
      this.state.progress.reduce(
        (acc, cur) => acc || cur.state !== "done",
        true
      );

    const uploadableFilesPresent =
      this.state.files.length > 0 &&
      this.state.progress.length < this.state.files.length;

    const fileList = (
      <Collapse in={this.state.showCompleted}>
        <ul>
          {this.state.files.map((file, idx) => (
            <li key={`${file.name}-${idx}`}>
              {file.name}
              &nbsp;
              {this.state.progress[idx] &&
              this.state.progress[idx].state === "done"
                ? "✔"
                : ""}
              {!this.state.progress[idx] ? (
                <a
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    this.removeFileFromList(file);
                  }}
                >
                  <span className="icon-trash-empty" />
                </a>
              ) : this.state.progress[idx].state === "done" ? (
                <ProgressBar now={100} />
              ) : (
                <ProgressBar
                  animated
                  now={this.state.progress[idx].percentage}
                />
              )}
            </li>
          ))}
        </ul>
      </Collapse>
    );

    const rejectedFiles = (
      <Collapse in={this.state.showRejected}>
        <ul>
          {this.state.rejected.map((str, idx) => (
            <li key={idx}>{str}</li>
          ))}
        </ul>
      </Collapse>
    );

    const serverProgress = (
      <Query
        query={UPLOAD_PROGRESS}
        variables={{ token: this.state.uploadToken }}
        pollInterval={3000}
      >
        {({ loading, error, data, stopPolling }: any) => {
          if (loading) {
            return (<>"Verarbeite..."</>);
          }

          if (error || data.uploadProgress.error) {
            if (this.state.errorMsg !== "") {
              return (<>{this.state.errorMsg}</>);
            }

            stopPolling();

            return (<>Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.</>);
          }

          if (data.uploadProgress.finished) {
            stopPolling();

            return (
              <p>
                Verarbeitung abgeschlossen. Vorgangsnummer:
                <Link
                  onClick={() => {
                    this.setState({
                      isProcessing: false,
                      showServerProgress: false,
                    });
                    this.props.onHide();
                  }}
                  to={`/vorgang/${data.uploadProgress.processNumber}`}
                >
                  {data.uploadProgress.processNumber}
                </Link>
              </p>
            );
          }

          return (
            <>
              <p>
                Verarbeite (dies kann ein paar Minuten dauern)...
                <div className="animate-spin icon-cog" />
              </p>
            </>
          );
        }}
      </Query>
    );

    return (
      <Modal
        show={this.props.show}
        onHide={() => {
          this.props.onHide();
          this.setState((prev: any) => ({
            ...prev,
            progress: [],
            files: [],
          }));
        }}
        size="lg"
        aria-labelledby="upload-dropzone-title"
        centered
        backdrop="static"
        className="upload-dropzone"
      >
        <Modal.Header>
          <Modal.Title id="upload-dropzone-title">Belege hochladen</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Dropzone
            onDropAccepted={this.onDropAccepted}
            onDropRejected={this.onDropRejected}
            accept={["image/jpeg", "image/png", "application/pdf"]}
            disabled={isUploading || this.state.showServerProgress}
            ref={dropzoneRef}
          >
            {({ getRootProps, getInputProps }) => {
              return (
                <section>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div id="dropzone-body">
                      <h1>
                        <span className="icon-kvaplus" /> Mach Plus
                      </h1>
                      <p style={{ fontWeight: 400 }}>
                        Ziehen Sie alle Belege für einen Abrechnungsvorgang
                        hierher, oder klicken Sie um Dateien auszuwählen.
                      </p>
                      <p>
                        Der Upload mehrerer Belege pro Patient ist möglich
                        &mdash;{" "}
                        <span style={{ fontWeight: "bold" }}>
                          bitte beachten Sie jedoch einen getrennten
                          Beleg-Upload für unterschiedliche Patienten.
                        </span>
                      </p>
                    </div>
                    <aside>
                      <h5 style={{ textAlign: "center" }}>
                        {isUploading &&
                          "Bitte schließen Sie das Fenster nicht."}
                      </h5>
                      {this.state.showServerProgress && serverProgress}
                    </aside>
                  </div>
                  <aside>{fileList}</aside>
                  <aside>{rejectedFiles}</aside>
                  <Button
                    onClick={() => {
                      if (!uploadableFilesPresent) {
                        dropzoneRef.current && dropzoneRef.current.open();
                      } else {
                        this.startUpload();
                      }
                    }}
                    disabled={isUploading || this.state.showServerProgress}
                  >
                    {!uploadableFilesPresent && "Dateien hochladen"}
                    {uploadableFilesPresent && "Upload beginnen"}
                  </Button>
                </section>
              );
            }}
          </Dropzone>
        </Modal.Body>

        <Modal.Footer>
          <Button
            disabled={isUploading}
            onClick={() => {
              this.props.onHide();
              this.setState((prev: any) => ({
                ...prev,
                progress: [],
                files: [],
                uploadToken: "",
                showServerProgress: false,
              }));
            }}
            variant="primary"
          >
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
